import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  FiCalendar, FiVideo, FiPhone, FiUsers, FiMail,
  FiCheck, FiX, FiClock, FiRefreshCw, FiArrowRight,
  FiSend, FiGlobe, FiLink, FiExternalLink
} from 'react-icons/fi';
import { API_URL } from '../../../utils/authUtils';

/**
 * SessionPanel - Handles CONSENTED, SCHEDULED, CONNECTED states.
 *
 * Sub-states:
 * - CONSENTED: Schedule a new session (with calendar invite options)
 * - SCHEDULED: View session details, reschedule, cancel, complete
 * - CONNECTED: View completed session, feedback status, close request
 */

const SESSION_TYPES = [
  { value: 'video_call', label: 'Video Call', icon: FiVideo },
  { value: 'phone_call', label: 'Phone Call', icon: FiPhone },
  { value: 'in_person', label: 'In Person', icon: FiUsers },
  { value: 'async_email', label: 'Async Email', icon: FiMail },
];

// Common US timezones grouped at top, then all others
const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const getTimezoneLabel = (tz) => {
  try {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' });
    const parts = fmt.formatToParts(now);
    const abbrev = parts.find(p => p.type === 'timeZoneName')?.value || '';
    return `${tz.replace(/_/g, ' ')} (${abbrev})`;
  } catch {
    return tz.replace(/_/g, ' ');
  }
};

const buildGoogleCalUrl = (session) => {
  if (!session.scheduled_at) return null;
  const start = new Date(session.scheduled_at);
  const end = new Date(start.getTime() + (session.duration_minutes || 30) * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const title = encodeURIComponent(`Introduction Session`);
  const location = encodeURIComponent(session.meeting_link || '');
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&location=${location}`;
};

const buildOutlookCalUrl = (session) => {
  if (!session.scheduled_at) return null;
  const start = new Date(session.scheduled_at);
  const end = new Date(start.getTime() + (session.duration_minutes || 30) * 60000);
  const title = encodeURIComponent(`Introduction Session`);
  const location = encodeURIComponent(session.meeting_link || '');
  return `https://outlook.live.com/calendar/0/action/compose?rru=addevent&subject=${title}&startdt=${start.toISOString()}&enddt=${end.toISOString()}&location=${location}`;
};

const SessionPanel = ({ requestDetail, requestId, onRequestUpdated, authHeaders }) => {
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Schedule form
  const [sessionType, setSessionType] = useState('video_call');
  const [scheduledAt, setScheduledAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Calendar invite options
  const [timezoneName, setTimezoneName] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
  });
  const [customMeetingLink, setCustomMeetingLink] = useState('');
  const [sendInvites, setSendInvites] = useState(true);
  const [testMode, setTestMode] = useState(false);
  const [inviteResults, setInviteResults] = useState(null);

  // Build timezone options list
  const timezoneOptions = useMemo(() => {
    let allTimezones;
    try {
      allTimezones = Intl.supportedValuesOf('timeZone');
    } catch {
      allTimezones = COMMON_TIMEZONES;
    }
    const common = COMMON_TIMEZONES.filter(tz => allTimezones.includes(tz));
    const others = allTimezones.filter(tz => !COMMON_TIMEZONES.includes(tz));
    return { common, others };
  }, []);

  // Reschedule
  const [showReschedule, setShowReschedule] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // Complete
  const [showComplete, setShowComplete] = useState(null);
  const [actualDuration, setActualDuration] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [completeLoading, setCompleteLoading] = useState(false);

  // Cancel
  const [showCancel, setShowCancel] = useState(null);
  const [cancelTestMode, setCancelTestMode] = useState(false);

  // Status update
  const [statusLoading, setStatusLoading] = useState(false);

  // Transition
  const [transitionLoading, setTransitionLoading] = useState(false);

  const getErrorMessage = (err, defaultMsg) => {
    if (err.response?.data) {
      const detail = err.response.data.detail;
      if (typeof detail === 'string') return detail;
      if (detail?.message) return detail.message;
      if (detail?.error) return detail.error;
    }
    return defaultMsg;
  };

  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/sessions`,
        authHeaders()
      );
      setSessions(res.data.items || []);
    } catch (err) {
      if (err.response?.status !== 404) {
        setError(getErrorMessage(err, 'Failed to load sessions'));
      }
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [requestId]);

  const scheduleSession = async () => {
    if (!scheduledAt) {
      setError('Please select a date and time');
      return;
    }
    setScheduleLoading(true);
    setError(null);
    setInviteResults(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/schedule`,
        {
          session_type: sessionType,
          scheduled_at: new Date(scheduledAt).toISOString(),
          duration_minutes: durationMinutes,
          timezone: timezoneName,
          meeting_link: (sessionType !== 'video_call' && customMeetingLink.trim()) ? customMeetingLink.trim() : null,
          send_invites: sendInvites,
          test_mode: testMode,
        },
        authHeaders()
      );
      const data = res.data;
      let msg = 'Session scheduled successfully';
      if (sendInvites && (data.requester_invite || data.target_invite)) {
        const rSent = data.requester_invite?.email_sent;
        const tSent = data.target_invite?.email_sent;
        if (testMode) {
          msg += ` — Test invites sent to cdgreaves@outlook.com (${(rSent ? 1 : 0) + (tSent ? 1 : 0)} of 2)`;
        } else {
          const sent = (rSent ? 1 : 0) + (tSent ? 1 : 0);
          msg += ` — Calendar invites sent (${sent} of 2)`;
        }
        setInviteResults({ requester: data.requester_invite, target: data.target_invite });
      }
      setSuccessMsg(msg);
      setScheduledAt('');
      setCustomMeetingLink('');
      onRequestUpdated();
      fetchSessions();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('A session already exists for this request');
        fetchSessions();
      } else {
        setError(getErrorMessage(err, 'Failed to schedule session'));
      }
    } finally {
      setScheduleLoading(false);
    }
  };

  const rescheduleSession = async (sessionId) => {
    if (!rescheduleDate) {
      setError('Please select a new date');
      return;
    }
    setRescheduleLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/brokerage/sessions/${sessionId}/reschedule`,
        {
          scheduled_at: new Date(rescheduleDate).toISOString(),
          reason: rescheduleReason || null,
          send_invites: sendInvites,
          test_mode: testMode,
        },
        authHeaders()
      );
      let msg = 'Session rescheduled successfully';
      const data = res.data;
      if (sendInvites && (data.requester_invite || data.target_invite)) {
        const sent = (data.requester_invite?.email_sent ? 1 : 0) + (data.target_invite?.email_sent ? 1 : 0);
        msg += ` — Updated invites sent (${sent} of 2)`;
      }
      setSuccessMsg(msg);
      setShowReschedule(null);
      setRescheduleDate('');
      setRescheduleReason('');
      onRequestUpdated();
      fetchSessions();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reschedule session'));
    } finally {
      setRescheduleLoading(false);
    }
  };

  const markComplete = async (sessionId) => {
    setCompleteLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/sessions/${sessionId}/complete`,
        {
          actual_duration_minutes: actualDuration ? parseInt(actualDuration, 10) : null,
          notes: completeNotes || null,
        },
        authHeaders()
      );
      setSuccessMsg('Session marked as completed');
      setShowComplete(null);
      setActualDuration('');
      setCompleteNotes('');
      onRequestUpdated();
      fetchSessions();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to complete session'));
    } finally {
      setCompleteLoading(false);
    }
  };

  const updateSessionStatus = async (sessionId, newStatus, notes = null, statusTestMode = false) => {
    setStatusLoading(true);
    setError(null);
    try {
      const res = await axios.patch(
        `${API_URL}/api/staff/brokerage/sessions/${sessionId}/status`,
        {
          session_status: newStatus,
          notes: notes,
          test_mode: statusTestMode,
        },
        authHeaders()
      );
      const data = res.data;
      let msg = `Session ${newStatus} successfully`;
      if (newStatus === 'cancelled' && (data.requester_cancel || data.target_cancel)) {
        const sent = [data.requester_cancel?.email_sent && 'mentee', data.target_cancel?.email_sent && 'mentor'].filter(Boolean);
        if (sent.length > 0) msg += ` — cancellation emails sent to ${sent.join(' & ')}`;
        if (statusTestMode) msg += ' (test mode)';
      }
      setSuccessMsg(msg);
      setShowCancel(null);
      setCancelTestMode(false);
      onRequestUpdated();
      fetchSessions();
    } catch (err) {
      setError(getErrorMessage(err, `Failed to update session to ${newStatus}`));
    } finally {
      setStatusLoading(false);
    }
  };

  const transitionRequest = async (targetStatus) => {
    setTransitionLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/transition`,
        {
          target_status: targetStatus,
          row_version: requestDetail.row_version,
        },
        authHeaders()
      );
      onRequestUpdated();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: Modified by another user. Refreshing...');
        onRequestUpdated();
      } else {
        setError(getErrorMessage(err, `Failed to transition to ${targetStatus}`));
      }
    } finally {
      setTransitionLoading(false);
    }
  };

  const getSessionTypeIcon = (type) => {
    const found = SESSION_TYPES.find(t => t.value === type);
    if (!found) return FiCalendar;
    return found.icon;
  };

  const getSessionTypeLabel = (type) => {
    const found = SESSION_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getCountdown = (dateStr) => {
    if (!dateStr) return null;
    const target = new Date(dateStr);
    const now = new Date();
    const diffMs = target - now;

    if (diffMs < 0) return { text: 'Past due', urgent: true };

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return { text: `${days}d ${hours % 24}h away`, urgent: days <= 1 };
    if (hours > 0) return { text: `${hours}h away`, urgent: true };
    return { text: 'Starting soon', urgent: true };
  };

  const currentStatus = requestDetail.status;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Session Management</h3>
        <p className="text-xs text-gray-500">
          {currentStatus === 'CONSENTED' && 'Schedule a session between the requester and target.'}
          {currentStatus === 'SCHEDULED' && 'Manage the upcoming session. Mark as completed when done.'}
          {currentStatus === 'CONNECTED' && 'Session completed. Review feedback and close when ready.'}
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {successMsg && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-2 text-green-500 hover:text-green-700">&times;</button>
        </div>
      )}

      {sessionsLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          <span className="ml-2 text-sm text-gray-500">Loading sessions...</span>
        </div>
      ) : (
        <>
          {/* Schedule Form - CONSENTED state with no active sessions */}
          {currentStatus === 'CONSENTED' && !sessions.some(s => s.session_status === 'scheduled' || s.session_status === 'rescheduled') && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 uppercase mb-4">Schedule Session</p>

              {/* Session Type */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-2">Session Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {SESSION_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setSessionType(type.value)}
                        className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                          sessionType === type.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date/Time */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time</label>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Duration */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10) || 30)}
                  min={5}
                  max={180}
                  className="w-32 text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Timezone */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <FiGlobe className="w-3.5 h-3.5" /> Timezone
                </label>
                <select
                  value={timezoneName}
                  onChange={(e) => setTimezoneName(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <optgroup label="Common (US)">
                    {timezoneOptions.common.map(tz => (
                      <option key={tz} value={tz}>{getTimezoneLabel(tz)}</option>
                    ))}
                  </optgroup>
                  <optgroup label="All Timezones">
                    {timezoneOptions.others.map(tz => (
                      <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Custom Meeting Link (for non-video sessions) */}
              {sessionType !== 'video_call' && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                    <FiLink className="w-3.5 h-3.5" /> Meeting Link or Dial-in (optional)
                  </label>
                  <input
                    type="text"
                    value={customMeetingLink}
                    onChange={(e) => setCustomMeetingLink(e.target.value)}
                    placeholder="https://... or phone number"
                    className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}
              {sessionType === 'video_call' && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <FiVideo className="w-3.5 h-3.5" /> A video meeting room will be auto-generated
                  </p>
                </div>
              )}

              {/* Send invites checkbox */}
              <div className="mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendInvites}
                    onChange={(e) => setSendInvites(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    <FiSend className="w-3.5 h-3.5" /> Send calendar invites to both parties
                  </span>
                </label>
              </div>

              {/* Test mode checkbox (amber-themed) */}
              {sendInvites && (
                <div className="mb-4 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={testMode}
                      onChange={(e) => setTestMode(e.target.checked)}
                      className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm text-amber-800 font-medium">Test Mode</span>
                  </label>
                  {testMode && (
                    <p className="text-xs text-amber-700 mt-1 ml-6">
                      Both invite emails will be sent to <strong>cdgreaves@outlook.com</strong> — one formatted for the requester (mentee), one for the target (mentor).
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={scheduleSession}
                disabled={scheduleLoading || !scheduledAt}
                className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
              >
                <FiCalendar className="w-4 h-4" />
                {scheduleLoading ? 'Scheduling...' : 'Schedule Session'}
              </button>

              {/* Invite delivery results */}
              {inviteResults && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs space-y-1">
                  <p className="font-medium text-blue-800">Calendar Invite Delivery:</p>
                  {inviteResults.requester && (
                    <p className={inviteResults.requester.email_sent ? 'text-green-700' : 'text-red-700'}>
                      Requester (mentee): {inviteResults.requester.email_sent ? '✓ Sent' : `✗ Failed: ${inviteResults.requester.email_error}`}
                      {inviteResults.requester.email_sent && <span className="text-gray-500"> → {inviteResults.requester.email_recipient}</span>}
                    </p>
                  )}
                  {inviteResults.target && (
                    <p className={inviteResults.target.email_sent ? 'text-green-700' : 'text-red-700'}>
                      Target (mentor): {inviteResults.target.email_sent ? '✓ Sent' : `✗ Failed: ${inviteResults.target.email_error}`}
                      {inviteResults.target.email_sent && <span className="text-gray-500"> → {inviteResults.target.email_recipient}</span>}
                    </p>
                  )}
                  {!inviteResults.requester && !inviteResults.target && (
                    <p className="text-gray-600">No email addresses found for either party.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Session Cards */}
          {sessions.map((session) => {
            const TypeIcon = getSessionTypeIcon(session.session_type);
            const countdown = getCountdown(session.scheduled_at);
            const isScheduled = session.session_status === 'scheduled';
            const isCompleted = session.session_status === 'completed';
            const isCancelled = session.session_status === 'cancelled';
            const isNoShow = session.session_status === 'no_show';

            return (
              <div key={session.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TypeIcon className={`w-5 h-5 ${isCompleted ? 'text-green-500' : isCancelled || isNoShow ? 'text-red-500' : 'text-indigo-500'}`} />
                    <span className="text-sm font-medium text-gray-800">{getSessionTypeLabel(session.session_type)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      isCompleted ? 'bg-green-100 text-green-700' :
                      isCancelled ? 'bg-red-100 text-red-700' :
                      isNoShow ? 'bg-orange-100 text-orange-700' :
                      isScheduled ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {session.session_status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </div>
                  {session.duration_minutes && (
                    <span className="text-xs text-gray-400">{session.duration_minutes} min</span>
                  )}
                </div>

                {/* Session Details */}
                <div className="space-y-2 text-sm">
                  {session.scheduled_at && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-gray-700">
                        {new Date(session.scheduled_at).toLocaleString(undefined, {
                          weekday: 'short', month: 'short', day: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                          ...(session.timezone ? { timeZone: session.timezone } : {})
                        })}
                      </span>
                      {session.timezone && (
                        <span className="text-xs text-gray-400">
                          {(() => { try { return new Intl.DateTimeFormat('en-US', { timeZone: session.timezone, timeZoneName: 'short' }).formatToParts(new Date(session.scheduled_at)).find(p => p.type === 'timeZoneName')?.value; } catch { return ''; } })()}
                        </span>
                      )}
                      {countdown && isScheduled && (
                        <span className={`text-xs ${countdown.urgent ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                          ({countdown.text})
                        </span>
                      )}
                    </div>
                  )}

                  {/* Meeting Link */}
                  {session.meeting_link && (
                    <div className="flex items-center gap-2">
                      <FiLink className="w-3.5 h-3.5 text-green-500" />
                      <a
                        href={session.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-700 hover:text-green-800 underline text-sm flex items-center gap-1"
                      >
                        Join Meeting <FiExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}

                  {/* Add to Calendar links */}
                  {session.scheduled_at && (isScheduled || session.session_status === 'rescheduled') && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">Add to:</span>
                      <a
                        href={buildGoogleCalUrl(session)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Google Calendar
                      </a>
                      <a
                        href={buildOutlookCalUrl(session)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Outlook
                      </a>
                    </div>
                  )}

                  {session.completed_at && (
                    <div className="flex items-center gap-2">
                      <FiCheck className="w-3.5 h-3.5 text-green-500" />
                      <span className="text-gray-700">
                        Completed {new Date(session.completed_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                        })}
                      </span>
                      {session.actual_duration_minutes && (
                        <span className="text-xs text-gray-500">({session.actual_duration_minutes} min actual)</span>
                      )}
                    </div>
                  )}
                  {session.requester_name && (
                    <div className="text-xs text-gray-500">
                      Requester: <span className="text-gray-700">{session.requester_name}</span>
                      {session.target_name && <> &mdash; Target: <span className="text-gray-700">{session.target_name}</span></>}
                    </div>
                  )}
                  {session.notes && (
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs text-gray-500 uppercase mb-0.5">Notes</p>
                      <p className="text-sm text-gray-700">{session.notes}</p>
                    </div>
                  )}
                </div>

                {/* Feedback tracking for completed sessions */}
                {isCompleted && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 uppercase mb-2">Feedback Status</p>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-1.5">
                        {session.requester_feedback_submitted ? (
                          <FiCheck className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <FiClock className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        <span className="text-xs text-gray-600">
                          Requester: {session.requester_feedback_submitted ? 'Submitted' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {session.target_feedback_submitted ? (
                          <FiCheck className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <FiClock className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        <span className="text-xs text-gray-600">
                          Target: {session.target_feedback_submitted ? 'Submitted' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions for scheduled/rescheduled sessions */}
                {(isScheduled || session.session_status === 'rescheduled') && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setShowComplete(session.id)}
                        disabled={statusLoading || completeLoading}
                        className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        <FiCheck className="w-3.5 h-3.5" /> Mark Completed
                      </button>
                      <button
                        onClick={() => setShowReschedule(session.id)}
                        disabled={rescheduleLoading}
                        className="px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                      >
                        <FiRefreshCw className="w-3.5 h-3.5" /> Reschedule
                      </button>
                      <button
                        onClick={() => setShowCancel(session.id)}
                        disabled={statusLoading}
                        className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Mark as no-show?')) {
                            updateSessionStatus(session.id, 'no_show');
                          }
                        }}
                        disabled={statusLoading}
                        className="px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 disabled:opacity-50"
                      >
                        No-Show
                      </button>
                    </div>

                    {/* Cancel form */}
                    {showCancel === session.id && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg space-y-3">
                        <p className="text-xs font-medium text-red-800">Cancel Session</p>
                        <p className="text-xs text-red-600">This will cancel the session and send cancellation emails to both parties (with .ics calendar removal).</p>
                        {session.ics_uid && (
                          <label className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                            <input
                              type="checkbox"
                              checked={cancelTestMode}
                              onChange={(e) => setCancelTestMode(e.target.checked)}
                              className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                            />
                            <div>
                              <span className="text-xs font-medium text-amber-800">Test Mode</span>
                              <p className="text-xs text-amber-600">Redirect both cancellation emails to cdgreaves@outlook.com</p>
                            </div>
                          </label>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateSessionStatus(session.id, 'cancelled', null, cancelTestMode)}
                            disabled={statusLoading}
                            className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            {statusLoading ? 'Cancelling...' : 'Confirm Cancel'}
                          </button>
                          <button
                            onClick={() => { setShowCancel(null); setCancelTestMode(false); }}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Back
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Complete form */}
                    {showComplete === session.id && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg space-y-3">
                        <p className="text-xs font-medium text-green-800">Complete Session</p>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Actual Duration (minutes)</label>
                          <input
                            type="number"
                            value={actualDuration}
                            onChange={(e) => setActualDuration(e.target.value)}
                            placeholder={String(session.duration_minutes || 30)}
                            min={1}
                            max={480}
                            className="w-32 text-sm border border-gray-300 rounded-lg p-2"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Notes</label>
                          <textarea
                            value={completeNotes}
                            onChange={(e) => setCompleteNotes(e.target.value)}
                            rows={2}
                            className="w-full text-sm border border-gray-300 rounded-lg p-2"
                            placeholder="Session notes..."
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => markComplete(session.id)}
                            disabled={completeLoading}
                            className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {completeLoading ? 'Completing...' : 'Confirm Complete'}
                          </button>
                          <button
                            onClick={() => { setShowComplete(null); setActualDuration(''); setCompleteNotes(''); }}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Reschedule form */}
                    {showReschedule === session.id && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                        <p className="text-xs font-medium text-blue-800">Reschedule Session</p>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">New Date & Time</label>
                          <input
                            type="datetime-local"
                            value={rescheduleDate}
                            onChange={(e) => setRescheduleDate(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-lg p-2"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Reason (optional)</label>
                          <textarea
                            value={rescheduleReason}
                            onChange={(e) => setRescheduleReason(e.target.value)}
                            rows={2}
                            className="w-full text-sm border border-gray-300 rounded-lg p-2"
                            placeholder="Reason for rescheduling..."
                          />
                        </div>

                        {/* Send invites + test mode for reschedule */}
                        <div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sendInvites}
                              onChange={(e) => setSendInvites(e.target.checked)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-xs text-gray-700 flex items-center gap-1">
                              <FiSend className="w-3 h-3" /> Send updated invites to both parties
                            </span>
                          </label>
                        </div>
                        {sendInvites && (
                          <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={testMode}
                                onChange={(e) => setTestMode(e.target.checked)}
                                className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                              />
                              <span className="text-xs text-amber-800 font-medium">Test Mode</span>
                            </label>
                            {testMode && (
                              <p className="text-xs text-amber-700 mt-1 ml-6">
                                Both emails → <strong>cdgreaves@outlook.com</strong>
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2">
                          <button
                            onClick={() => rescheduleSession(session.id)}
                            disabled={rescheduleLoading || !rescheduleDate}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            {rescheduleLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
                          </button>
                          <button
                            onClick={() => { setShowReschedule(null); setRescheduleDate(''); setRescheduleReason(''); }}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Transition / Close buttons */}
          {requestDetail.valid_transitions?.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 uppercase mb-3">Actions</p>
              <div className="flex flex-wrap gap-2">
                {requestDetail.valid_transitions?.includes('CONNECTED') && currentStatus === 'SCHEDULED' && (
                  <button
                    onClick={() => transitionRequest('CONNECTED')}
                    disabled={transitionLoading}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <FiArrowRight className="w-3.5 h-3.5" /> Mark Connected
                  </button>
                )}
                {requestDetail.valid_transitions?.includes('CLOSED') && (
                  <button
                    onClick={() => transitionRequest('CLOSED')}
                    disabled={transitionLoading}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    Close Request
                  </button>
                )}
                {requestDetail.valid_transitions?.includes('WITHDRAWN') && (
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to withdraw this request?')) {
                        transitionRequest('WITHDRAWN');
                      }
                    }}
                    disabled={transitionLoading}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    Withdraw
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SessionPanel;
